import React, { useState } from "react";
import { NaverTitle } from "../../../components/title/Title";
import { PageTitleContainer, BizInfoCard, BizContainer } from "../../../components/common";
import { overlay } from "overlay-kit";
import { AlertModal } from "components/modal/AlertModal";
import { useNaverShopSearchQuery } from "views/naver/service/hooks/queries";
import { inject, observer } from "mobx-react";
import { Button } from "components/ui/Button";
import Text from "components/ui/Text";
import { useNaverConnectMutation } from "views/naver/service/hooks/mutations";
import { useNaverShopInfoInvalidate } from "views/naver/hooks/useNaverInvalidate";
import { useHistory } from "react-router";

interface Props {
  rootStore?: any;
}

export const ShopExistView = inject("rootStore")(
  observer(({ rootStore }: Props) => {
    const { searchStore } = rootStore;
    const history = useHistory();
    const [currentPage, setCurrentPage] = useState(1);
    const invalidateNaverShopInfo = useNaverShopInfoInvalidate();

    // 매장 조회
    const shopCd = searchStore.getShopCd;
    const naverUniqueId = searchStore.getNaverUniqueId;
    const { data: shopSearchData } = useNaverShopSearchQuery(
      rootStore,
      {
        shopCd,
        page: currentPage,
        size: 5,
        includeNpayUnregistered: true,
      },
      { enabled: !!naverUniqueId }
    );

    // 연동
    const { mutate: naverConnect } = useNaverConnectMutation(rootStore);

    const shops = shopSearchData?.data?.content || [];
    const pageInfo = shopSearchData?.data?.page;
    const totalPages = pageInfo?.totalPages || 1;
    const currentPageNumber = pageInfo?.pageNumber || 1;

    // 페이징 핸들러
    const handlePreviousPage = () => {
      if (currentPage > 1) {
        console.log('currentPage', currentPage);
        setCurrentPage(currentPage - 1);
      }
    };

    const handleNextPage = () => {
      if (currentPage < totalPages) {
        console.log('currentPage', currentPage);
        setCurrentPage(currentPage + 1);
      }
    };

    // 주문 사용 관리
    const handleOrderManagement = (businessId: number) => {
      history.push(`/naver/bizitem?businessId=${businessId}&connected=false`);
    };

    const handleConnect = (shopName: string, businessId: number) => {
      overlay.open(({ isOpen, close }) => (
        <AlertModal
          open={isOpen}
          title="매장을 연동할까요?"
          subTit1={`선택하신 ${shopName}과 배달매니저를 연동합니다.`}
          subTit2="연동 이후에는 배달매니저에서 네이버 주문을 처리할 수 있습니다."
          leftBtnOnClick={close}
          leftBtnVariant="secondaryPrimary"
          leftBtnTxt="닫기"
          rightBtnOnClick={() => {
            close();
            naverConnect(
              {
                shopCd: searchStore.getShopCd,
                businessId,
              },
              {
                onSuccess: async data => {
                  overlay.open(({ isOpen }) => (
                    <AlertModal
                      open={isOpen}
                      title="연동이 완료되었어요"
                      subTit1="네이버 주문하기 필수 사용 설정을 위해 이동할게요"
                      rightBtnOnClick={() => {
                        overlay.closeAll();
                        invalidateNaverShopInfo();
                        handleOrderManagement(businessId);
                      }}
                      rightBtnVariant="primaryWhite"
                      rightBtnTxt="이동하기"
                    />
                  ));
                },
                onError: error => {
                  overlay.open(({ isOpen, close }) => (
                    <AlertModal
                      open={isOpen}
                      title="연동에 실패했어요"
                      subTit1="알 수 없는 오류가 발생했습니다."
                      subTit2="잠시 후 다시 시도해주세요."
                      rightBtnOnClick={close}
                      rightBtnVariant="secondaryPrimary"
                      rightBtnTxt="닫기"
                    />
                  ));
                },
              }
            );
          }}
          rightBtnVariant="primaryWhite"
          rightBtnTxt="연동하기"
        />
      ));
    };

    return (
      <>
        <PageTitleContainer>
          <NaverTitle
            title="연동할 매장을 선택하세요"
            subTitle="배달매니저와 네이버 주문 매장이 연동되면 배달매니저에서 주문을 처리할 수 있어요"
          />
        </PageTitleContainer>

        {/* 매장 리스트 */}
        <BizContainer>
          {shops.map((shop: any, index: number) => (
            <BizInfoCard
              key={shop.businessId}
              iconSrc={shop.image}
              iconAlt={`${shop.shopNm} 이미지`}
              title={shop.shopNm}
              address={`${shop.addr} ${shop.addrDtl || ""}`.trim()}
              phone={shop.telNo}
              buttons={[
                {
                  text: "연동하기",
                  variant: "primaryWhite",
                  onClick: () => handleConnect(shop.shopNm, shop.businessId),
                },
              ]}
            />
          ))}
        </BizContainer>

        {/* 페이징 - 다음 페이지가 있을 때만 표시 */}
        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-3 pt-6">
            <Text fontSize={14} fontWeight={400} color="#51525C">
              {currentPageNumber} / {totalPages}
            </Text>
            <Button
              fullWidth={false}
              variant="secondaryLight"
              size="sm"
              padding="12px 16px"
              disabled={currentPage === 1}
              onClick={handlePreviousPage}
            >
              이전
            </Button>
            <Button
              fullWidth={false}
              variant="secondaryLight"
              size="sm"
              padding="12px 16px"
              disabled={currentPage >= totalPages}
              onClick={handleNextPage}
            >
              다음
            </Button>
          </div>
        )}
      </>
    );
  })
);
